import {DialogPositioningBehavior, DialogType} from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import {CreateDatasourceExtractTaskActionService} from '~src/extract/actions/CreateDatasourceExtractTaskAction';
import {CreateExtractTasksMode} from '~src/extract/actions/CreateExtractTaskAction';
import {incrementRouterSpaceReactKey} from '~src/spacesPlatform/actions/IncrementRouterSpaceReactKeyAction';

import {getAppStore} from '../../app/AppStore';
import {PublishDatasourceSuccessDialog,} from '../../app/content/PublishDataSourceSuccessDialog';
import {Messages} from '../../codegen/Localize';
import {ModalInstance, ReactModalService} from '../../commonInfra/dialogs/ReactModalService';
import {IDataSourceDetailInfo} from '../../datasource/models/IDataSourceInfo';
import {RefreshOptionType} from '../../datasource/resources/Datasources';
import {ChangeDatasourceRefreshModeAction} from '../../datasource/services/ChangeDatasourceRefreshModeAction';
import {IDatasourcePublishResult, ISyncPublishResult} from '../../datasources/services/DatasourcePublishResultService';
import {RemoteRefreshAgents} from '../../extractRefresh/resources/RemoteRefreshAgents';
import {L10n} from '../../ts/L10n/L10n';
import {FeatureFlags} from '../FeatureFlags';
import {RefreshModeTypes} from '../ServerApi/types/IRemoteRefreshProperties';
import {PostPublishStep} from './PostPublishStep';

export namespace PublishDatasourceAction {

  export function handlePublishedDatasource(countOfSchedules: number, publishResult: IDatasourcePublishResult, datasource: IDataSourceDetailInfo) {
    if (publishResult && publishResult.preferredRefreshOption) {
      // Make our best attempt to fire this off, even when the window closes.
      const unloadListener = () =>
      RemoteRefreshAgents.completeWorkflowDuringWindowUnload(publishResult.remoteAgentWorkflowId);
      window.addEventListener('unload', unloadListener, false);

      let handlingResult: Promise<any>;

      if (countOfSchedules === 0) {
        if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.LiveConnection) {
          // If it is a live connection check if it was published with live query sync
            if (publishResult.publishedWithLQS){
              // reach here if url is: datasources/id/publishresult?publishedWithLQS=y
              handlingResult = displaySuccessDialog(datasource, PostPublishStep.OnlineLiveQuerySync);
              // $MARK bmoulin TFSID:581383:As a User I can chose which LQA agent from post-publish success dialog
            } else {
              handlingResult = displaySuccessDialog(datasource, PostPublishStep.Done);
            }
        } else if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.Server) {
          // If it is a server-refresh
          handlingResult = displaySuccessDialog(datasource, PostPublishStep.ServerRefresh)
            .then(() => ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Server, null, true, false))
            .then(() => CreateDatasourceExtractTaskActionService.forDatasource(datasource, CreateExtractTasksMode.AllowScheduleOnServer))
            .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));

        } else if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.OnlineSync) {
          let shouldScheduleOnlyOnClient = false;
          if (!FeatureFlags.bridgeServerTriggeredClientSideRefreshForFileBasedDatasourceEnabled && datasource.isFileBased) {
            shouldScheduleOnlyOnClient = true;
          }

          const createExtractTasksMode = shouldScheduleOnlyOnClient ? CreateExtractTasksMode.AllowScheduleOnlyOnClient : CreateExtractTasksMode.AllowScheduleOnServerAndClient;
          const refreshMode = shouldScheduleOnlyOnClient ? RefreshModeTypes.Remote : RefreshModeTypes.Server;

          handlingResult = displaySuccessDialog(datasource, PostPublishStep.ServerTriggeredClientRefresh)
            .then(() => ChangeDatasourceRefreshModeAction.execute(datasource, refreshMode, null, true, false))
            .then(() => CreateDatasourceExtractTaskActionService.forDatasource(datasource, createExtractTasksMode)
              .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey())));
        }

        handlingResult.finally(() => {
          // Clear this from the scope so the dialog won't appear again if the user navigates back to this state
          if (publishResult) {
            publishResult.preferredRefreshOption = null;
          }
        });
      }

      handlingResult.finally(() => {
        window.removeEventListener('unload', unloadListener, false);
        RemoteRefreshAgents.completeWorkflow(publishResult.remoteAgentWorkflowId);
      });
    }
  }

  function displaySuccessDialog(datasource: IDataSourceDetailInfo, postPublishStep: PostPublishStep): Promise<any> {
    let modalInstance: ModalInstance;
    const dialogProps = {
      postPublishStep: postPublishStep,
      datasourceSuccessMessage: Messages.publishDatasourceSuccessDialogTextPublished({ DATASOURCE_NAME: L10n.elideIfLong(datasource.name) }),
      close: () => modalInstance && modalInstance.close()
    };

    modalInstance = ReactModalService.open({
      title:  Messages.publishDatasourceSuccessDialogTitleComplete(),
      type: DialogType.LightweightTitleBar,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogContent: React.createElement(PublishDatasourceSuccessDialog, dialogProps),
      showCloseButton: true,
      dialogWidth: 500,
      dismissDialogOnCloseAction: true,
      disableAutoFocus: true,
    });

    return Promise.resolve(modalInstance.result).finally(modalInstance.dismiss);
  }

  export function canReserveSyncWithWorkflow(publishResult: ISyncPublishResult) {
    return !_.isEmpty(publishResult.remoteAgentWorkflowId);
  }
}

export type PublishDatasourceAction = typeof PublishDatasourceAction;
